import { Component, Input, Output, EventEmitter } from '@angular/core';
import { HttpService } from '../../store/http/http.service';
import { Store } from '@ngrx/store';
import {
  saveScene,
  selectCurrentScene,
  StudioScene,
  StudioService,
} from '../../element-editor/store/studio';
import { cloneDeep } from 'lodash';
import { RootLayoutCell } from '../../element-editor/store/studio/layout/layout-cell';
import { PromptResponseService } from './prompt-response.service';
import { take } from 'rxjs/operators';

@Component({
  selector: 'nw-prompt',
  templateUrl: './prompt.component.html',
  styleUrls: ['./prompt.component.scss'],
})
export class PromptComponent {
  /** The descriptor object to be refined (animation, layout, background, etc.) */
  @Input() currentObject: any;

  /** Textual description of the domain, passed to the backend LLM as system context */
  @Input() context: string = '';

  /** Emits the refined descriptor returned by the backend */
  @Output() result = new EventEmitter<any>();

  prompt = '';
  isLoading = false;

  currentScene: StudioScene | null = null;
  constructor(
    private httpService: HttpService,
    private readonly store: Store,
    private readonly studioService: StudioService,
    private readonly promptResponseService: PromptResponseService,
  ) {
    this.store.select(selectCurrentScene).subscribe(scene => {
      this.currentScene = cloneDeep(scene);
      if (!scene) return;
    });
  }

  basePrompt = `
You are an AI assistant that helps users make small visual adjustments to a single scene in a video editor.

You will receive a JSON object representing the current scene (StudioScene). Your job is to apply the user's requested changes and return the modified scene data.

--- SCENE STRUCTURE ---

The scene contains two closely related fields that together define the layout:

1. "shapes": Record<string, ShapeDescriptor>
   Each key is a UUID identifying one element. Each shape has a "cell" with its own x/y/w/h, but when a "layoutDefinition" is present this is the authoritative source of truth for layout.

2. "layoutDefinition": LayoutCellDefinition (the primary subject of most layout changes)
   A recursive tree that describes how the canvas is split into regions. The engine derives each shape's actual cell dimensions from this tree, so changing layoutDefinition is the correct way to reposition or resize elements.

--- LAYOUT DEFINITION TREE ---

Each node in the tree is a LayoutCellDefinition:

interface LayoutCellDefinition {
  // Container split type (present on branch nodes)
  type?: 'row' | 'column';   // 'row' = children placed left-to-right, 'column' = top-to-bottom
  gap?: number;              // normalized gap between children (0–1)

  // Inherited from StudioCell (applies to this node's region)
  x?: number;    // horizontal position (0–1); usually omitted — computed from the tree
  y?: number;    // vertical position (0–1); usually omitted — computed from the tree
  w?: number;    // width (0–1); set on the root or when overriding a child's proportion
  h?: number;    // height (0–1); set on the root or when overriding a child's proportion
  padding?: number | [number, number];  // [paddingX, paddingY] or uniform value (normalized 0–1)
  align?: 'left' | 'center' | 'right';
  vAlign?: 'top' | 'center' | 'bottom';
  fontSize?: number;
  background?: { type: 'color' | 'gradient', color: string, opacity: number, radius: number };

  // Leaf node only (points to a shape)
  shape?: string;  // UUID of the shape this cell controls

  // Recursive children
  children?: LayoutCellDefinition[];
}

How sizing works:
- The root node always covers the full canvas (w:1, h:1, x:0, y:0).
- A 'row' container divides its width equally among children (minus gaps and padding).
- A 'column' container divides its height equally among children (minus gaps and padding).
- To give children unequal sizes, set explicit "w" (for rows) or "h" (for columns) on each child node.
- Leaf nodes (those with "shape") receive their dimensions from the computed parent region.

Example — two text shapes side by side:
{
  "type": "row",
  "gap": 0.02,
  "padding": 0.05,
  "children": [
    { "shape": "<uuid-of-text-1>" },
    { "shape": "<uuid-of-text-2>" }
  ]
}

Example — image on left, two stacked texts on right:
{
  "type": "row",
  "children": [
    { "shape": "<uuid-of-image>", "w": 0.5 },
    {
      "type": "column",
      "w": 0.5,
      "children": [
        { "shape": "<uuid-of-title>" },
        { "shape": "<uuid-of-subtitle>" }
      ]
    }
  ]
}

Each shape has a "cell" that defines its position and size using normalized coordinates (values between 0 and 1, relative to the canvas dimensions):
  - x: horizontal position (0 = left edge)
  - y: vertical position (0 = top edge)
  - w: width (1 = full canvas width)
  - h: height (1 = full canvas height)
  - align: 'left' | 'center' | 'right'  (horizontal text alignment)
  - vAlign: 'top' | 'center' | 'bottom' (vertical text alignment)
  - padding: number (inner padding)

--- SHAPE TYPES ---

1. Text shape (identified by having a "text" field):
   Editable properties:
   - text: string (the displayed text content)
   - fill: string (text color as hex, e.g. "#ffffff")
   - fontFamily: string (font name)
   - fontWeight: number (e.g. 400, 700)
   - fontStyle: 'normal' | 'italic' | 'oblique'
   - align: 'left' | 'center' | 'right'
   - vAlign: 'top' | 'center' | 'bottom'
   - capitalization: 'normal' | 'capital'
   - lineHeight: number (multiplier, e.g. 1.2)
   - cell: { x, y, w, h, align, vAlign, padding } (position and size)
   - background: { type: 'color' | 'gradient', color: string, opacity: number, radius: number, padding: number }
   - outline: string (outline color as hex)
   - outlineWidth: number

2. Image shape (identified by having an "imageId" field):
   Editable properties:
   - cell: { x, y, w, h } (position and size)
   - colorize: string (hex color to tint the image, e.g. "#ff0000"; omit to remove tint)
   - radius: number (corner radius in pixels)

3. Video shape (identified by having a "videoInstanceId" field):
   Editable properties:
   - cell: { x, y, w, h } (position and size)

--- BACKGROUND IMAGE ---

The scene may have a "background" field at the top level:

{
  "type": "image",
  "image": {
    "url": string,              // DO NOT change — references the actual asset
    "fit": "cover" | "contain" | "stretch",  // how the image fills the canvas
    "alpha": number,            // opacity 0–1
    "scale": number,            // zoom multiplier applied on top of fit (e.g. 1.2 = 20% zoom in); omit or use 1 for no extra zoom
    "translate": [number, number]  // pixel offset [dx, dy] applied after fit and scale; positive dx moves right, positive dy moves down
  }
}

When the user asks to zoom, pan, reposition, or adjust the background image:
- Modify only "scale" and/or "translate" inside "background.image".
- Do NOT change "url", "fit", or "alpha" unless explicitly asked.
- ALWAYS include the original "url" value unchanged when returning a background change — NEVER omit it.
- "scale" is a multiplier: 1 = no change, 1.5 = 50% larger, 0.8 = 20% smaller.
- "translate" is in canvas pixels (the canvas is typically 1920×1080). E.g. [100, 0] shifts the image 100px to the right.
- Omit "background" from the response if it was not changed.

--- STRICT RULES ---

- Do NOT change shape UUID keys.
- Do NOT change the "imageId" or "videoInstanceId" values — these reference the actual media assets.
- Do NOT add or remove shapes; only modify existing ones.
- Do NOT include any fields not listed above as editable.
- NEVER omit "background.image.url" from the response if it is present in the current scene — always copy the original value unchanged.
- Only modify what the user explicitly asks to change; leave everything else untouched.
- When the change is about layout/positioning, modify "layoutDefinition" rather than individual shape cells.
- All "shape" UUID references inside layoutDefinition must match existing shape UUIDs from the input.

--- RESPONSE FORMAT ---

Respond with valid JSON only. No explanation, no markdown, no extra text.
The response is a partial scene descriptor — a plain object containing ONLY the top-level field(s) that were changed:

{
  "layoutDefinition": { ... }   // if layout was changed
}

or

{
  "background": { ... }         // if background was changed
}

or

{
  "shapes": { ... }             // if shape properties were changed
}

CRITICAL RULES for the response:
- Include ONLY the field(s) the user asked to change. Do NOT include any other fields.
- If the user asked to change the background → return ONLY "background": {...}.
- If the user asked to change layout → return ONLY "layoutDefinition": {...}.
- If the user asked to change shape properties → return ONLY "shapes": {...} (include all shapes, modified and unmodified).
- NEVER include "layoutDefinition" when the user only asked to change the background or shapes.
- NEVER include "background" when the user only asked to change the layout or shapes.
- NEVER include "shapes" when the user only asked to change the background or layout.
`;

  async submit() {
    if (!this.prompt.trim()) return;

    this.isLoading = true;
    console.log('current scene', this.currentScene);
    try {
      const response = await this.httpService
        ._patch<{ result: any }>('ai/descriptor/nova', {
          inputObject: this.currentScene,
          prompt: this.basePrompt + ' \n' + this.prompt,
        })
        .pipe(take(1))
        .toPromise();

      if (response?.result) {
        const validation = this.promptResponseService.parse(response.result);
        if (!validation.valid) {
          console.error('Invalid AI response format:', validation.errors);
          return;
        }
        const desc = validation.value;
        console.log('updated descriptor', desc);

        const { layoutDefinition, background } = desc as unknown as StudioScene;

        if (layoutDefinition) {
          console.log('new layout definition', layoutDefinition);
          const shape = this.studioService.currentSceneShape;
          const layoutCell = new RootLayoutCell(
            this.currentScene,
            layoutDefinition,
            shape ? { W: shape.screenWidth, H: shape.screenHeight } : undefined,
          );

          Object.entries(this.currentScene.shapes).forEach(([id, shape]) => {
            if (!layoutCell.shapeCells[id]) {
              return;
            }
            console.log('setting-cell', layoutCell.shapeCells[id]);
            shape.cell = layoutCell.shapeCells[id];
          });
        }

        this.saveScene({
          ...(layoutDefinition ? { layoutDefinition } : {}),
          ...(background ? { background } : {}),
        });
        this.result.emit(desc);
        this.prompt = '';
      }
    } finally {
      this.isLoading = false;
    }
  }

  saveScene(override: Partial<StudioScene>) {
    this.store.dispatch(
      saveScene({
        value: {
          ...this.currentScene,
          ...override,
        },
      }),
    );
  }
}
